import React, { useState } from 'react';
import { Modal, Upload, Button, List, Typography, Space, Tabs, message } from 'antd';
import { UploadOutlined, DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TabPane } = Tabs;

const FileUploadModal = ({ 
  visible, 
  onCancel, 
  onUpload, 
  title = "Files to upload", 
  sessionId, 
  requestContext = {}
}) => {
  const [fileList, setFileList] = useState({});
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const safeRequestContext = requestContext || {};

  // Determine if this is a group upload or single file upload
  const isGroupUpload = requestContext?.isGroupUpload || false;
  const fileGroups = requestContext?.fileGroups || [];
  
  // If it's a single file upload, create a virtual group for consistent handling
  const effectiveGroups = isGroupUpload ? fileGroups : [{
    id: 'default',
    title: safeRequestContext.title || title || 'File', // Provide multiple fallbacks
    description: safeRequestContext.description || '',
    required: safeRequestContext.required || false,
    allowMultiple: safeRequestContext.allowMultiple || false,
    maxFiles: safeRequestContext.maxFiles || 5,
    acceptedFileTypes: safeRequestContext.acceptedFileTypes || '*',
    documentType: safeRequestContext.documentType || 'generic'
  }];

  // Handle file selection
  const handleFileChange = (info, groupId = 'default') => {
    setFileList(prev => ({
      ...prev,
      [groupId]: info.fileList
    }));
  };

  // Handle file removal
  const handleRemove = (file, groupId = 'default') => {
    setFileList(prev => {
      const updated = { ...prev };
      if (updated[groupId]) {
        updated[groupId] = updated[groupId].filter(f => f.uid !== file.uid);
      }
      return updated;
    });
  };

  // Handle the upload action
  const handleUpload = async () => {
    // Validate if required files are present
    for (const group of effectiveGroups) {
      if (group.required && (!fileList[group.id] || fileList[group.id].length === 0)) {
        message.warning(`Please select a file for ${group.title}`);
        return;
      }
    }

    if (Object.values(fileList).flat().length === 0) {
      message.warning('Please select at least one file first');
      return;
    }

    try {
      setUploading(true);
      
      // Prepare all files from all groups
      const allFiles = [];
      const fileMetadata = {};
      
      Object.entries(fileList).forEach(([groupId, files]) => {
        files.forEach(file => {
          allFiles.push(file.originFileObj);
          
          // Find the group this file belongs to
          const group = effectiveGroups.find(g => g.id === groupId);
          const groupMetadata = group?.metadata || {};
          
          // Store metadata about which group this file belongs to
          fileMetadata[file.originFileObj.name] = { 
            groupId, 
            documentType: group?.documentType || 'generic',
            ...groupMetadata,
            ...(file.metadata || {})
          };
        });
      });
      

      console.log('???????????   ', requestContext.agent_key)
      console.log('???????????   ', requestContext.component_map)


      // Add metadata about file groups to context
      const uploadContext = {
        ...requestContext,
        fileMetadata,
        isGroupUpload,
        agent_key: requestContext.agent_key, // Make sure this is passed through
        component_map: requestContext.component_map // Include component map
    };
      
      // Call the onUpload callback with files and context
      const success = await onUpload(allFiles, uploadContext, sessionId);
      
      if (success) {
        setFileList({});
        message.success(`${allFiles.length} file(s) uploaded successfully`);
        onCancel(true); // Close with success flag
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      message.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFileList({});
    onCancel(false); // Close with cancel flag
  };

  // Render tabs for group uploads or single view for regular uploads
  const renderUploadContent = () => {
    if (isGroupUpload && fileGroups.length > 1) {
      // Multi-group upload with tabs
      return (
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          style={{ marginBottom: 16 }}
        >
          <TabPane tab="All Files" key="all">
            <div style={{ padding: '16px' }}>
              {effectiveGroups.map(group => (
                <div key={group.id} style={{ marginBottom: '24px' }}>
                  <Text strong>{group.title}</Text>
                  {group.required && <Text type="danger"> *</Text>}
                  <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                    <Text type="secondary">{group.description}</Text>
                  </div>
                  <Upload
                    multiple={group.allowMultiple}
                    beforeUpload={() => false}
                    fileList={fileList[group.id] || []}
                    onChange={(info) => handleFileChange(info, group.id)}
                    onRemove={(file) => handleRemove(file, group.id)}
                    accept={group.acceptedFileTypes}
                    maxCount={group.maxFiles}
                  >
                    <Button icon={<UploadOutlined />}>
                      {group.allowMultiple ? 'Add Files' : 'Select File'}
                    </Button>
                  </Upload>
                </div>
              ))}
            </div>
          </TabPane>
          {effectiveGroups.map(group => (
            <TabPane tab={group.title} key={group.id}>
              <div style={{ padding: '16px' }}>
                <Text strong>{group.title}</Text>
                {group.required && <Text type="danger"> *</Text>}
                <div style={{ marginTop: '4px', marginBottom: '12px' }}>
                  <Text type="secondary">{group.description}</Text>
                </div>
                <Upload
                  multiple={group.allowMultiple}
                  beforeUpload={() => false}
                  fileList={fileList[group.id] || []}
                  onChange={(info) => handleFileChange(info, group.id)}
                  onRemove={(file) => handleRemove(file, group.id)}
                  accept={group.acceptedFileTypes}
                  maxCount={group.maxFiles}
                >
                  <Button icon={<UploadOutlined />}>
                    {group.allowMultiple ? 'Add Files' : 'Select File'}
                  </Button>
                </Upload>
              </div>
            </TabPane>
          ))}
        </Tabs>
      );
    } else {
      // Simple view (single file type or single group)
      const group = effectiveGroups[0];
      return (
        <div style={{ padding: '16px' }}>
          {group.description && (
            <div style={{ marginBottom: '16px' }}>
              <Text>{group.description}</Text>
            </div>
          )}
          <Upload
            multiple={group.allowMultiple}
            beforeUpload={() => false}
            fileList={fileList[group.id] || []}
            onChange={(info) => handleFileChange(info, group.id)}
            onRemove={(file) => handleRemove(file, group.id)}
            accept={group.acceptedFileTypes}
            maxCount={group.maxFiles}
          >
            <Button icon={<UploadOutlined />}>
              {group.allowMultiple ? 'Add Files' : 'Select File'}
            </Button>
          </Upload>
        </div>
      );
    }
  };

  // Count total files across all groups
  const totalFiles = Object.values(fileList).reduce((count, files) => count + files.length, 0);

  return (
    <Modal
      open={visible}
      title={title}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={uploading}
          onClick={handleUpload}
          disabled={totalFiles === 0}
        >
          Upload {totalFiles > 0 ? `${totalFiles} file${totalFiles > 1 ? 's' : ''}` : ''}
        </Button>,
      ]}
      width={600}
    >
      {renderUploadContent()}
      
      {/* Summary of selected files */}
      {totalFiles > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Text strong>Selected files:</Text>
          <List
            size="small"
            bordered
            dataSource={Object.entries(fileList).flatMap(([groupId, files]) => 
              files.map(file => ({ 
                ...file, 
                groupId,
                groupName: effectiveGroups.find(g => g.id === groupId)?.title || groupId
              }))
            )}
            renderItem={file => (
              <List.Item
                actions={[
                  <DeleteOutlined key="delete" onClick={() => handleRemove(file, file.groupId)} />
                ]}
              >
                <Space>
                  <PaperClipOutlined />
                  <Text>{file.name}</Text>
                  <Text type="secondary">{(file.size / 1024).toFixed(2)} kB</Text>
                  {isGroupUpload && effectiveGroups.length > 1 && <Text type="secondary">[{file.groupName}]</Text>}
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}
    </Modal>
  );
};

export default FileUploadModal;